import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  ShoppingBag,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Package,
  Eye,
  ArrowUpRight,
  ShoppingCart,
  Store,
  BarChart3,
  Globe,
  Sparkles,
  Plus,
  ExternalLink } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend } from
'recharts';
import { Link } from 'react-router-dom';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const CHANNEL_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export const EcommerceDashboard = () => {const { t } = useTranslation("ecommerce");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/dashboard/stats`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  const summary = stats?.summary || {};
  const today = stats?.today || {};
  const topProducts = stats?.top_products || [];
  const salesByChannel = stats?.sales_by_channel || [];
  const revenueTrend = stats?.revenue_trend || [];
  const recentOrders = stats?.recent_orders || [];

  return (
    <div className="space-y-6" data-testid="ecommerce-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
              <ShoppingBag className="h-7 w-7 text-white" />
            </div>{t("ecommerce", "Ecommerce")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageYourOnline", "Manage your online stores, orders, and sales channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/ecommerce/stores">
            <Button variant="outline" className="gap-2">
              <Store className="h-4 w-4" />{t("myStores", "My Stores")}

            </Button>
          </Link>
          <Link to="/ecommerce/builder">
            <Button className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
              <Plus className="h-4 w-4" />{t("createStore", "Create Store")}

            </Button>
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">{t("totalRevenue", "Total Revenue")}</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(summary.total_revenue || 0)}</p>
                <div className="flex items-center gap-1 mt-2 text-emerald-100">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm">+{summary.revenue_change || 0}{t("thisMonth", "% this month")}</span>
                </div>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <DollarSign className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
        </Card>

        <Card className="card-elevated hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">{t("totalOrders", "Total Orders")}</p>
                <p className="text-3xl font-bold mt-1">{summary.total_orders || 0}</p>
                <div className="flex items-center gap-1 mt-2 text-emerald-600">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm">+{summary.orders_change || 0}%</span>
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <ShoppingCart className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">{t("conversionRate", "Conversion Rate")}</p>
                <p className="text-3xl font-bold mt-1">{summary.conversion_rate || 0}%</p>
                <div className="flex items-center gap-1 mt-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span className="text-sm">{today.visitors || 0}{t("visitorsToday", "visitors today")}</span>
                </div>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">{t("avgOrderValue", "Avg. Order Value")}</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(summary.avg_order_value || 0)}</p>
                <div className="flex items-center gap-1 mt-2 text-amber-600">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-sm">{today.orders || 0}{t("ordersToday", "orders today")}</span>
                </div>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <Package className="h-8 w-8 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Revenue Trend */}
        <Card className="lg:col-span-2 card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />{t("revenueTrend", "Revenue Trend")}

            </CardTitle>
            <CardDescription>{t("monthlyRevenuePerformance", "Monthly revenue performance")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrend}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(v) => formatCurrency(v)}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                  
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)" />
                  
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Sales by Channel */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />{t("salesByChannel", "Sales by Channel")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={salesByChannel}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="revenue"
                    nameKey="channel"
                    label={({ channel, percent }) => `${(percent * 100).toFixed(0)}%`}>
                    
                    {salesByChannel.map((entry, index) =>
                    <Cell key={`cell-${index}`} fill={CHANNEL_COLORS[index % CHANNEL_COLORS.length]} />
                    )}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Products */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />{t("topSellingProducts", "Top Selling Products")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topProducts.map((product, idx) =>
              <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-muted-foreground">{product.sales}{t("unitsSold", "units sold")}</p>
                    </div>
                  </div>
                  <span className="font-mono font-semibold text-emerald-600">
                    {formatCurrency(product.revenue)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-600" />{t("recentOrders", "Recent Orders")}

              </CardTitle>
            </div>
            <Link to="/ecommerce/orders">
              <Button variant="ghost" size="sm" className="gap-1">{t("viewAll", "View All")}

                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentOrders.slice(0, 5).map((order, idx) =>
              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-medium">
                      {order.customer_name?.charAt(0) || 'C'}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{order.order_number}</p>
                      <p className="text-xs text-muted-foreground">{order.customer_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold text-sm">{formatCurrency(order.total)}</p>
                    <Badge
                    variant="outline"
                    className={`text-xs ${
                    order.status === 'completed' ? 'border-emerald-500 text-emerald-600' :
                    order.status === 'shipped' ? 'border-blue-500 text-blue-600' :
                    order.status === 'paid' ? 'border-purple-500 text-purple-600' :
                    'border-amber-500 text-amber-600'}`
                    }>
                    
                      {order.status}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="card-elevated bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold">{t("quickActions", "Quick Actions")}</h3>
              <p className="text-sm text-muted-foreground">{t("manageYourEcommerce", "Manage your ecommerce operations")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/ecommerce/builder">
                <Button variant="outline" className="gap-2">
                  <Store className="h-4 w-4" />{t("storefrontBuilder", "Storefront Builder")}

                </Button>
              </Link>
              <Link to="/ecommerce/products">
                <Button variant="outline" className="gap-2">
                  <Package className="h-4 w-4" />{t("manageProducts", "Manage Products")}

                </Button>
              </Link>
              <Link to="/ecommerce/orders">
                <Button variant="outline" className="gap-2">
                  <ShoppingCart className="h-4 w-4" />{t("viewOrders", "View Orders")}

                </Button>
              </Link>
              <Button variant="outline" className="gap-2">
                <ExternalLink className="h-4 w-4" />{t("visitStore", "Visit Store")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default EcommerceDashboard;